import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {IconName} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {useLocalize} from '../../../../../srcCommon/internationalization/internationalizationContext';
import {isCustomFieldEnumType} from '../../../../helpers/customFieldHelpers';
import {computeCustomFieldIconName} from '../../../../helpers/customFieldViewHelpers';
import {CrmHubspotFieldModel, HubspotFieldTypesEnum} from '../../../../models/api/crmHubspotFieldModel';
import {CrmHubspotMetadataModel} from '../../../../models/api/crmHubspotMetadataModel';
import {CrmSyncMappingCustomFieldTypesEnum, CrmSyncSourcesEnum} from '../../../../models/api/crmSyncModel';
import {CustomFieldsEnum} from '../../../../models/api/customFieldModel';
import {CrmPreviewParentPanelId} from '../../../../store/panels/contacts/contactsManager/crmPreview/crmPreviewActions';
import {Localizer} from '../../../../store/stateless/internationalizationSelectors';
import {Skeleton} from '../../../common/skeleton';
import {PreviewCrmSyncBannerContainer} from '../banners/previewCrmSyncBannerContainer';
import {CrmMappingContainer} from '../common/crmMappingContainer';
import {
  computeCrmSyncMappingFromCustomFieldMapping,
  computePlaceholderFieldIconName,
  computePlaceholderFieldName,
  CrmToFrontCustomFieldMapping,
  OnUpdateMappedFieldHandler,
} from '../common/crmSyncHelpers';
import {CrmSyncDialogStep} from '../crmSyncDialogStep';
import {HubspotCustomFieldsDropdown} from './dropdowns/hubspotCustomFieldsDropdown';
import {HubspotDefaultCustomFieldIdsEnum, HubspotRequiredDefaultFields} from './hubspotHelpers';

/*
 * Props.
 */

interface HubspotCrmSyncMappingStepProps {
  panelId: CrmPreviewParentPanelId;
  crmSyncId?: number;
  hidePreview: boolean;
  isDisabled: boolean;
  isAdmin: boolean;
  mappedFields: ReadonlyArray<CrmToFrontCustomFieldMapping>;
  hubspotMetadata?: CrmHubspotMetadataModel;
  hubspotFields?: ReadonlyArray<CrmHubspotFieldModel>;
  isLoading?: boolean;
  onUpdateMappedField: OnUpdateMappedFieldHandler;
  onAddMappedField: HandlerOf<CrmToFrontCustomFieldMapping>;
  onRemoveMappedField: HandlerOf<string>;
}

/*
 * Styles.
 */

const StyledHubspotPlaceholderDiv = styled.div`
  color: ${(p) => p.theme.greys.shade60};
`;

/*
 * Component.
 */

export const HubspotCrmSyncMappingStep: FC<HubspotCrmSyncMappingStepProps> = (props) => {
  const {
    panelId,
    crmSyncId,
    isDisabled,
    hidePreview = false,
    mappedFields,
    isLoading,
    isAdmin,
    hubspotMetadata,
    hubspotFields,
    onRemoveMappedField,
    onUpdateMappedField,
  } = props;
  const localize = useLocalize();

  return (
    <CrmSyncDialogStep
      step={2}
      title={renderTitle()}
      description={renderDescription()}
      isDisabled={isDisabled}
      isLoading={isLoading}
      renderLoadingState={renderLoadingState}
    >
      <CrmMappingContainer
        source={CrmSyncSourcesEnum.HUBSPOT}
        requiredFields={HubspotRequiredDefaultFields}
        mappedFields={mappedFields}
        onRemoveMappedField={onRemoveMappedField}
        onUpdateMappedField={onUpdateMappedField}
        renderCrmCustomFieldMissingError={renderCrmMissingError}
        renderCrmPlaceholder={renderPlaceholder}
        isDisabled={isDisabled}
        isAdmin={isAdmin}
        computeCrmCustomFieldName={(customFieldId) =>
          computeHubspotCustomFieldName(customFieldId, localize, hubspotFields)
        }
        computeCrmCustomFieldIcon={(customFieldId) =>
          computeHubspotCustomFieldIcon(customFieldId, hubspotFields, hubspotMetadata)
        }
        renderAddFieldDropdown={(popoverProps, crmFieldId) =>
          renderAddFieldDropdown(popoverProps, crmFieldId, props)
        }
        computeFrontTypesFromCrmCustomField={(customFieldId) =>
          computeFrontTypesFromCrmCustomField(customFieldId, hubspotFields, hubspotMetadata)
        }
      />
      {maybeRenderAccountPreviewBanner(
        panelId,
        isLoading || isDisabled || hidePreview,
        mappedFields,
        crmSyncId,
      )}
    </CrmSyncDialogStep>
  );
};

/*
 * Helpers.
 */

function maybeRenderAccountPreviewBanner(
  panelId: CrmPreviewParentPanelId,
  shouldHide: boolean,
  mappedFields: ReadonlyArray<CrmToFrontCustomFieldMapping>,
  crmSyncId?: number,
) {
  if (!crmSyncId || shouldHide) {
    return null;
  }
  return (
    <PreviewCrmSyncBannerContainer
      panelId={panelId}
      crmSyncId={crmSyncId}
      crmSyncSource={CrmSyncSourcesEnum.HUBSPOT}
      mapping={computeCrmSyncMappingFromCustomFieldMapping([
        ...HubspotRequiredDefaultFields,
        ...mappedFields,
      ])}
    />
  );
}

function computeHubspotCustomFieldName(
  customFieldId: string,
  localize: Localizer,
  hubspotFields?: ReadonlyArray<CrmHubspotFieldModel>,
): string | undefined {
  if (!hubspotFields || hubspotFields.length === 0) {
    return computePlaceholderFieldName(customFieldId, localize);
  }
  const hubspotField = hubspotFields.find((field) => field.id === customFieldId);
  return hubspotField?.label;
}

function renderLoadingState() {
  return <Skeleton width="100%" height="56px" />;
}

function renderAddFieldDropdown(
  popoverProps: PopoverTransferProps,
  crmFieldId: string | undefined,
  props: HubspotCrmSyncMappingStepProps,
) {
  const {mappedFields, hubspotFields, hubspotMetadata, onAddMappedField, onUpdateMappedField} = props;
  const alreadySelectedIds = [...HubspotRequiredDefaultFields, ...mappedFields].map(
    (field) => field.crmFieldId,
  );

  const onSelect = (customField: CrmHubspotFieldModel) => {
    if (!crmFieldId) {
      onAddMappedField({crmFieldId: customField.id});
    } else {
      onUpdateMappedField(crmFieldId, undefined, customField.id);
    }
  };
  return (
    <HubspotCustomFieldsDropdown
      popoverProps={popoverProps}
      selectedCustomFieldId={crmFieldId}
      onSelect={onSelect}
      alreadySelectedIds={alreadySelectedIds}
      hubspotFields={hubspotFields}
      hubspotMetadata={hubspotMetadata}
      computeCustomFieldIcon={computeHubspotCustomFieldIcon}
    />
  );
}

function computeFrontTypesFromCrmCustomField(
  customFieldId: string,
  hubspotFields?: ReadonlyArray<CrmHubspotFieldModel>,
  hubspotMetadata?: CrmHubspotMetadataModel,
): ReadonlyArray<CustomFieldsEnum | CrmSyncMappingCustomFieldTypesEnum> {
  if (!hubspotMetadata || !hubspotFields) {
    return [];
  }
  const hubspotField = hubspotFields.find((field) => field.id === customFieldId);
  const mappings = hubspotMetadata.mapping[hubspotField?.type ?? HubspotFieldTypesEnum.STRING];

  // If the field is a domain, we need to allow the user to select email domain.
  if (hubspotField?.id === HubspotDefaultCustomFieldIdsEnum.DOMAINS) {
    return [CrmSyncMappingCustomFieldTypesEnum.DOMAINS, CustomFieldsEnum.STRING];
  }
  // If the field is a description, we need to allow the user to select description.
  if (hubspotField?.id === HubspotDefaultCustomFieldIdsEnum.DESCRIPTION) {
    return [CrmSyncMappingCustomFieldTypesEnum.DESCRIPTION, CustomFieldsEnum.STRING];
  }
  return mappings;
}

function renderCrmMissingError() {
  return (
    <Trans id="accounts-crm-dialog-hubspot-mapping-step-crm-field-missing">
      Field has been deleted in Hubspot.
    </Trans>
  );
}

function renderTitle() {
  return <Trans id="accounts-crm-dialog-hubspot-mapping-step-title">Map fields</Trans>;
}

function renderDescription() {
  return (
    <Trans id="accounts-crm-dialog-hubspot-mapping-step-description">
      Select the Hubspot fields you would like to sync to Front, and map them to a Front account custom field.
    </Trans>
  );
}

function computeHubspotCustomFieldIcon(
  customFieldId: string,
  hubspotFields?: ReadonlyArray<CrmHubspotFieldModel>,
  hubspotMetadata?: CrmHubspotMetadataModel,
): IconName | undefined {
  if (!hubspotFields || !hubspotMetadata || hubspotFields.length === 0) {
    return computePlaceholderFieldIconName(customFieldId);
  }

  const hubspotField = hubspotFields.find((field) => field.id === customFieldId);
  if (!hubspotField) {
    return computePlaceholderFieldIconName(customFieldId);
  }

  // If the field is a domain, we need a custom icon.
  if (hubspotField.id === HubspotDefaultCustomFieldIdsEnum.DOMAINS) {
    return computeCustomFieldIconName(CustomFieldsEnum.EMAIL_DOMAIN, VisualSizesEnum.SMALL);
  }

  // If we have more than 1 mapping type, try to find a non-string field to use for the icon.
  const validMappingIconTypes = hubspotMetadata.mapping[hubspotField.type].filter(isCustomFieldEnumType);

  if (validMappingIconTypes.length === 0) {
    return computePlaceholderFieldIconName(customFieldId);
  }

  const nonStringField = validMappingIconTypes.find(
    (customFieldType) => customFieldType !== CustomFieldsEnum.STRING,
  );
  if (nonStringField) {
    return computeCustomFieldIconName(nonStringField, VisualSizesEnum.SMALL);
  }
  return computeCustomFieldIconName(validMappingIconTypes[0], VisualSizesEnum.SMALL);
}

function renderPlaceholder() {
  return (
    <StyledHubspotPlaceholderDiv>
      <Trans id="accounts-crm-dialog-hubspot-mapping-step-missing">Select field to map from</Trans>
    </StyledHubspotPlaceholderDiv>
  );
}
